import * as THREE from 'three';
import { mixRgb, clamp, smoothstep } from '@/core/rng';
import { setAerialSky } from '@/render/aerial';

/** Gradient dome with cloud banding and a night star field, tone-mapped to match the scene. */
export function createSky(): THREE.Mesh {
  const mat = new THREE.ShaderMaterial({
    side: THREE.BackSide, depthWrite: false, depthTest: false, fog: false,
    uniforms: {
      top: { value: new THREE.Color() }, mid: { value: new THREE.Color() },
      bot: { value: new THREE.Color() }, glow: { value: new THREE.Color() },
      sunDir: { value: new THREE.Vector3(0, 1, 0) },
      expo: { value: 0.92 }, drift: { value: 0 }, night: { value: 0 },
    },
    vertexShader: `varying vec3 vW;
      void main(){ vW = normalize(position);
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }`,
    fragmentShader: `
      uniform vec3 top,mid,bot,glow,sunDir; uniform float expo,drift,night; varying vec3 vW;
      vec3 aces(vec3 x){ return clamp((x*(2.51*x+0.03))/(x*(2.43*x+0.59)+0.14),0.0,1.0); }
      float band(vec2 p){
        float v = sin(p.x*2.7+p.y*1.3)*0.5+0.5;
        v *= sin(p.x*1.13-p.y*2.1+1.7)*0.5+0.5;
        v *= sin(p.x*5.9+p.y*4.3-0.6)*0.5+0.5;
        return v; }
      float hash13(vec3 p){
        p = fract(p*443.8975);
        p += dot(p, p.yzx+19.19);
        return fract((p.x+p.y)*p.z); }
      // point stars from a cell hash on the dome direction: most cells are
      // empty; survivors get a position, a brightness and a twinkle phase.
      // grid 56 with a 1.2% gate leaves a few hundred on the upper dome.
      float stars(vec3 v){
        vec3 p = v*56.0;
        vec3 c = floor(p);
        float h = hash13(c);
        vec3 s = c + 0.2 + 0.6*vec3(hash13(c+1.7), hash13(c+9.1), hash13(c+23.3));
        float m = 1.0 - smoothstep(0.0, 0.7, length(p-s));
        float tw = 0.68 + 0.32*sin(drift*900.0 + h*289.0);
        return step(0.988, h) * m*m*m * (0.35 + 0.65*hash13(c+41.0)) * tw; }
      void main(){
        vec3 v = normalize(vW);
        float h = clamp(v.y*1.05+0.05,-1.0,1.0);
        vec3 c = h>0.0 ? mix(mid,top,pow(h,0.62)) : mix(mid,bot,clamp(-h*3.2,0.0,1.0));
        vec2 p = v.xz/max(0.16,abs(v.y))*0.5 + vec2(drift, drift*0.4);
        float cl = smoothstep(0.30,0.92,band(p)) * smoothstep(0.01,0.26,v.y) * 0.6;
        // cloud veils whiten by day but go moon-grey after dark, or they
        // would hang as bright smears on a black sky
        vec3 veil = mix(vec3(1.0), vec3(0.10,0.12,0.17), night);
        c = mix(c, mix(c, veil, 0.75), cl);
        // stars live on the upper dome only, behind the cloud veils, faded
        // in by the night factor — daylight drowns them completely
        float st = stars(v) * night * smoothstep(0.0, 0.10, v.y)
                 * (1.0 - clamp(cl*1.8, 0.0, 1.0));
        c += vec3(0.82, 0.88, 1.0) * st;
        float d = max(dot(v, normalize(sunDir)), 0.0);
        c += glow*pow(d,60.0)*1.6 + glow*pow(d,7.0)*0.18 + glow*cl*pow(d,3.0)*0.25;
        gl_FragColor = vec4(pow(aces(c*expo), vec3(1.0/2.2)), 1.0); }`,
  });
  const sky = new THREE.Mesh(new THREE.SphereGeometry(1, 40, 24), mat);
  sky.frustumCulled = false;
  sky.renderOrder = -1;
  return sky;
}

/**
 * The last sky `paintSky` painted, kept so anything that has to MATCH the sky
 * can ask it rather than approximate it.
 *
 * The sea is the reason this exists. A flat water surface at the altitudes
 * this game is played at spends its last few kilometres at grazing incidence,
 * where it stops being water-coloured and becomes a mirror of the sky — so
 * the correct colour for the far ocean is not "paler", it is *whatever the
 * sky is doing in the reflected direction*. Deriving it from `fog.color`
 * works (that is the horizon band, times 0.92–0.98) but throws away the sun
 * glow, which is exactly the term that makes water go bright silver toward
 * the sun and slate away from it.
 *
 * Keeping one source means the sea cannot drift from the sky as the sky is
 * tuned. Everything here is a pure read of state `paintSky` already computed.
 */
const state = {
  top: new THREE.Color(), mid: new THREE.Color(),
  bot: new THREE.Color(), glow: new THREE.Color(),
  sun: new THREE.Vector3(0, 1, 0), night: 0, expo: 0.92,
};

/** The tone curve the dome's own fragment shader ends with. */
function aces(x: number): number {
  const v = (x * (2.51 * x + 0.03)) / (x * (2.43 * x + 0.59) + 0.14);
  return Math.min(1, Math.max(0, v));
}

/**
 * The sky's colour along a view direction, **in DISPLAY space** — the same
 * value the dome's own shader writes to the framebuffer, tone-mapped and
 * gamma-encoded. That is deliberate: a caller mixing toward it after
 * `colorspace_fragment` gets "fully reflective means exactly the sky colour"
 * true by construction, with no chance of the two drifting apart.
 *
 * Cloud veils and stars are left out. They are dome dressing at specific
 * places on the sphere; a reflection wants the sky's *light*, and a mirrored
 * star field on the sea would be a bug rather than a feature.
 *
 * `dir` need not be normalised.
 */
export function skyColourAlong(dir: THREE.Vector3, out = new THREE.Color()): THREE.Color {
  const len = Math.hypot(dir.x, dir.y, dir.z) || 1;
  const vx = dir.x / len, vy = dir.y / len, vz = dir.z / len;
  // the same band split the dome uses: a lifted horizon, so the gradient's
  // pivot sits just above the true horizontal
  const h = Math.min(1, Math.max(-1, vy * 1.05 + 0.05));
  if (h > 0) out.copy(state.mid).lerp(state.top, Math.pow(h, 0.62));
  else out.copy(state.mid).lerp(state.bot, Math.min(1, Math.max(0, -h * 3.2)));
  // …and the sun's own glow, which is what the sea is really reflecting when
  // it goes silver. The tight lobe is dropped: a 60th-power highlight is the
  // disc itself, and a mirrored disc belongs to a specular term, not to this.
  const d = Math.max(0, vx * state.sun.x + vy * state.sun.y + vz * state.sun.z);
  const wide = Math.pow(d, 7) * 0.18;
  out.r += state.glow.r * wide;
  out.g += state.glow.g * wide;
  out.b += state.glow.b * wide;
  const e = state.expo;
  out.setRGB(
    Math.pow(aces(out.r * e), 1 / 2.2),
    Math.pow(aces(out.g * e), 1 / 2.2),
    Math.pow(aces(out.b * e), 1 / 2.2),
  );
  return out;
}

/**
 * The degenerate case of {@link skyColourAlong}: what distance dissolves
 * into, looking level at the horizon. Also display space.
 */
export function horizonColour(out = new THREE.Color()): THREE.Color {
  return skyColourAlong(HORIZON_DIR, out);
}
const HORIZON_DIR = new THREE.Vector3(0, 0, -1);

export function paintSky(
  sky: THREE.Mesh, sunDir: THREE.Vector3, fog: THREE.FogExp2 | THREE.Fog,
  /** 0 clear … 1 full grey lid — the weather field's overcast. */
  overcast = 0,
) {
  const t = clamp(sunDir.y, 0, 1);
  // one factor drives nightfall: the palette, the stars and (through mid) the
  // fog all follow it, so dusk is a single smooth slide with nothing popping
  const night = 1 - smoothstep(-0.04, 0.08, sunDir.y);
  // …and one drives the lid: every band greys toward cloud-light, so an
  // overcast is the whole sky's state and not a fog trick under a blue dome
  const lid = overcast * (1 - night * 0.6);
  const grey = (c: [number, number, number], k: number): [number, number, number] => {
    const l = (c[0] + c[1] + c[2]) / 3;
    return mixRgb(c, [l * 0.96, l * 0.98, l * 1.02], k);
  };
  const top = grey(mixRgb(mixRgb([18, 26, 48], [10, 24, 62], t), [4, 7, 16], night), lid * 0.55);
  const mid = grey(mixRgb(mixRgb([132, 102, 76], [70, 104, 142], t), [10, 15, 28], night), lid * 0.7);
  // below the horizon: warm ground-glow at dusk, deep-sea navy in daylight —
  // so the edge of the sea plane dissolves into "more ocean", not a table edge
  const bot = grey(mixRgb(mixRgb([200, 120, 60], [34, 56, 80], clamp(t * 1.6, 0, 1)), [7, 11, 20], night),
                   lid * 0.6);
  // the sun's glow cools to a dim moon-silver, so what little remains above
  // the horizon at night reads as moonlight rather than a buried sunset —
  // and a lid smothers it: no disc burns through a full overcast
  const gl = mixRgb(mixRgb(mixRgb([255, 168, 92], [255, 238, 208], t), [96, 104, 122], night),
                    [128, 132, 138], lid * 0.8);
  const u = (sky.material as THREE.ShaderMaterial).uniforms;
  u.top.value.setRGB(top[0] / 255, top[1] / 255, top[2] / 255);
  u.mid.value.setRGB(mid[0] / 255, mid[1] / 255, mid[2] / 255);
  u.bot.value.setRGB(bot[0] / 255, bot[1] / 255, bot[2] / 255);
  u.glow.value.setRGB(gl[0] / 255, gl[1] / 255, gl[2] / 255);
  u.sunDir.value.copy(sunDir);
  u.night.value = night;
  u.drift.value = performance.now() * 7.5e-6;
  // …and keep it, so `skyColourAlong` answers about the sky actually on
  // screen this frame rather than about a second, drifting copy of it
  state.top.copy(u.top.value);
  state.mid.copy(u.mid.value);
  state.bot.copy(u.bot.value);
  state.glow.copy(u.glow.value);
  state.sun.copy(sunDir).normalize();
  state.night = night;
  state.expo = u.expo.value as number;
  // atmospheric haze is the sky near the horizon — the SAME bands the dome
  // draws, handed to every fogged material (`render/aerial.ts`), which puts
  // them through the dome's own tone curve per sight line. `fog.color` keeps
  // the horizon's DISPLAY value for the materials that cannot take the bands
  // (a ShaderMaterial with fog: true and no fogSun): until 2026-08-18 it was
  // the linear band ×0.92, which three then mixed in display space — a haze
  // about half as bright as the sky it stood under.
  setAerialSky(state.sun, u.mid.value, u.glow.value, state.expo);
  horizonColour(fog.color);
}
